"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  Explanation,
  Formula,
  PairedDataInput,
  ResultTable,
  usePairedData,
  WorkingPanel,
} from "@/components/tools/stats-descriptive/ui";
import { XYPlot } from "@/components/tools/stats-regression/xy-plot";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  formatPRelation,
  formatPValue,
  formatStat,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";
import {
  type Alternative,
  classifyPairs,
  type KendallReport,
  kendallReport,
} from "@/lib/tool-kit/stats-regression/kendall";
import { apaBounded, apaPClause } from "@/lib/tool-kit/stats-research/format";

/** English strings. A translated content file overrides these through `ui`. */
export const KENDALL_TAU_CALCULATOR_STRINGS = {
  entryLabel: "Data entry",
  entryLists: "Separate X and Y lists",
  entryLines: 'Pairs — one "x, y" per line',
  xLabel: "X values (or ranks)",
  yLabel: "Y values (or ranks)",
  xPlaceholder: "1, 2, 3, 4, 5, 6, 7, 8, 9, 10",
  yPlaceholder: "2, 1, 4, 3, 7, 8, 5, 10, 6, 9",
  pairsLabel: "Paired data — one x, y pair per line",
  pairsPlaceholder: "1, 2\n2, 1\n3, 4",
  valuesCount: "one: value|other: values",
  pairsCount: "one: pair read|other: pairs read",
  skippedCount:
    "one: , {count} line without two numbers skipped|other: , {count} lines without two numbers skipped",
  tryExample: "Try an example",
  tryTiesExample: "Try an example with ties",
  exampleX: "1, 2, 3, 4, 5, 6, 7, 8, 9, 10",
  exampleY: "2, 1, 4, 3, 7, 8, 5, 10, 6, 9",
  examplePairs: "1, 2\n2, 1\n3, 4\n4, 3\n5, 7\n6, 8\n7, 5\n8, 10\n9, 6\n10, 9",
  exampleTiesX: "1, 2, 2, 3, 4, 5, 6, 8, 10, 12",
  exampleTiesY: "35, 46, 40, 52, 58, 57, 64, 66, 69, 68",
  exampleTiesPairs:
    "1, 35\n2, 46\n2, 40\n3, 52\n4, 58\n5, 57\n6, 64\n8, 66\n10, 69\n12, 68",
  clear: "Clear",
  mismatch:
    "The two lists differ in length (X: {x}, Y: {y}). Each x needs a matching y.",
  enterPrompt:
    "Enter at least three (x, y) pairs. Each variable needs at least two different values.",
  alternativeLabel: "Alternative hypothesis",
  altTwoSided: "Two-sided (τ ≠ 0)",
  altGreater: "Positive association (τ > 0)",
  altLess: "Negative association (τ < 0)",
  continuity: "Apply a continuity correction (normal approximation only)",
  results: "Results",
  tauB: "Kendall's τ-b",
  tauA: "Kendall's τ-a",
  pValue: "p-value",
  zScore: "z",
  exactTest: "Test",
  exactLabel: "Exact",
  normalLabel: "Normal approx.",
  pairsN: "Observations (n)",
  concordantCard: "Concordant pairs (C)",
  discordantCard: "Discordant pairs (D)",
  strong: "strong",
  moderate: "moderate",
  weak: "weak",
  positive: "positive",
  negative: "negative",
  interpretation:
    "τ-b = {tau}: a {strength} {direction} association — of the pairs of observations that are not tied, {share} are ordered the same way by both variables.",
  noAssociation:
    "τ-b = {tau}: essentially no association between the two orderings.",
  significant: "p {p}: statistically significant at the 5% level.",
  notSignificant: "p {p}: not statistically significant at the 5% level.",
  methodExact:
    "The p-value is exact: it comes from the distribution of the number of concordant pairs over all {n}! possible orderings (the same method as R's cor.test).",
  methodNormal:
    "The p-value uses the normal approximation to S = C − D with the tie-corrected variance (the same method as R's cor.test).",
  apaLabel: "APA-style result",
  apaTemplate: "Kendall’s τ_b = {tau}, p {p}, n = {n}",
  copyResult: "Copy result",
  copied: "Copied!",
  plotTitle: "Scatter plot",
  plotLabel: "Scatter plot of {n} paired observations",
  axisX: "x",
  axisY: "y",
  seriesPoints: "Observations",
  pairTableTitle: "Pair counts",
  colKind: "Kind of pair",
  colCount: "Pairs",
  kindConcordant: "Concordant (same order)",
  kindDiscordant: "Discordant (opposite order)",
  kindTiedX: "Tied on x only",
  kindTiedY: "Tied on y only",
  kindTiedBoth: "Tied on both",
  kindTotal: "All pairs, n(n − 1) ÷ 2",
  listTitle: "Every pair, classified",
  colPair: "Pair",
  colObs: "Observations (x, y)",
  colResult: "Verdict",
  pairObs: "({x1}, {y1}) and ({x2}, {y2})",
  pairIndex: "{i} and {j}",
  compareTitle: "Compared with other coefficients",
  colCoefficient: "Coefficient",
  colValue: "Value",
  rowSpearman: "Spearman's ρ",
  rowPearson: "Pearson's r",
  compareNote:
    "τ is usually smaller in size than ρ for the same data, so the two are not on the same scale: compare each with its own conventions.",
  workingTitle: "How it was calculated",
  workCounts:
    "n₀ = n(n − 1) ÷ 2 = {n} × {nm1} ÷ 2 = {pairs} pairs; C = {c}, D = {d}, so S = C − D = {s}",
  workTauA: "τ-a = S ÷ n₀ = {s} ÷ {pairs} = {tauA}",
  workTauB:
    "τ-b = S ÷ √((n₀ − n₁)(n₀ − n₂)) = {s} ÷ √(({pairs} − {t1}) × ({pairs} − {t2})) = {tauB}",
  workNoTies: "No ties, so τ-a and τ-b are equal.",
  workZ:
    "z = S ÷ √Var(S) = {s} ÷ √{variance} = {z}, with Var(S) corrected for ties",
  workZCc: "Continuity correction: z = (|S| − 1) ÷ √Var(S) = {z}",
} as const;

type Strings = typeof KENDALL_TAU_CALCULATOR_STRINGS;

function strengthLabel(tau: number, strings: Strings): string | null {
  const size = Math.abs(tau);
  if (size >= 0.5) {
    return strings.strong;
  }
  if (size >= 0.3) {
    return strings.moderate;
  }
  if (size >= 0.1) {
    return strings.weak;
  }
  return null;
}

function interpretation(
  report: KendallReport,
  fmt: NumberFormatter,
  strings: Strings
): string {
  const tau = formatStat(fmt, report.tauB);
  const strength = strengthLabel(report.tauB, strings);
  if (strength === null) {
    return t(strings.noAssociation, { tau });
  }
  const untied =
    report.counts.concordant + report.counts.discordant || report.pairs;
  const agree =
    report.tauB > 0 ? report.counts.concordant : report.counts.discordant;
  return t(strings.interpretation, {
    tau,
    strength,
    direction: report.tauB > 0 ? strings.positive : strings.negative,
    share: fmt(agree / untied, { style: "percent", maximumFractionDigits: 0 }),
  });
}

function kindLabel(kind: string, strings: Strings): string {
  const labels: Record<string, string> = {
    concordant: strings.kindConcordant,
    discordant: strings.kindDiscordant,
    tiedX: strings.kindTiedX,
    tiedY: strings.kindTiedY,
    tiedBoth: strings.kindTiedBoth,
  };
  return labels[kind] ?? kind;
}

function Working({
  report,
  fmt,
  strings,
  continuity,
}: {
  report: KendallReport;
  fmt: NumberFormatter;
  strings: Strings;
  continuity: boolean;
}) {
  const num = (value: number) => formatStat(fmt, value);
  const pairs = fmt(report.pairs);
  return (
    <WorkingPanel title={strings.workingTitle}>
      <Formula>
        {t(strings.workCounts, {
          n: fmt(report.n),
          nm1: fmt(report.n - 1),
          pairs,
          c: fmt(report.counts.concordant),
          d: fmt(report.counts.discordant),
          s: fmt(report.s),
        })}
      </Formula>
      <Formula>
        {t(strings.workTauA, {
          s: fmt(report.s),
          pairs,
          tauA: num(report.tauA),
        })}
      </Formula>
      {report.hasTies ? (
        <Formula>
          {t(strings.workTauB, {
            s: fmt(report.s),
            pairs,
            t1: fmt(report.tiesX),
            t2: fmt(report.tiesY),
            tauB: num(report.tauB),
          })}
        </Formula>
      ) : (
        <Explanation>{strings.workNoTies}</Explanation>
      )}
      {report.z === null ? null : (
        <Formula>
          {t(continuity ? strings.workZCc : strings.workZ, {
            s: fmt(report.s),
            variance: num(report.varianceS),
            z: num(report.z),
          })}
        </Formula>
      )}
    </WorkingPanel>
  );
}

export default function KendallTauCalculatorWidget() {
  const strings = useToolUi(KENDALL_TAU_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const data = usePairedData();
  const [alternative, setAlternative] = useState<Alternative>("two-sided");
  const [continuity, setContinuity] = useState(false);
  const num = (value: number) => formatStat(fmt, value);

  const report = data.mismatched
    ? null
    : kendallReport(data.xs, data.ys, { alternative, continuity });
  const showMismatch =
    data.mismatched && data.listXCount > 0 && data.listYCount > 0;
  const count = (n: number) => (
    <>
      {fmt(n)}
      {tPlural(strings.valuesCount, locale, n)}
    </>
  );
  const apa = report
    ? t(strings.apaTemplate, {
        tau: apaBounded(fmt, report.tauB, 2),
        p: apaPClause(fmt, report.p),
        n: fmt(report.n),
      })
    : "";

  return (
    <>
      <section className="space-y-4">
        <PairedDataInput
          data={data}
          idPrefix="kt"
          labels={{
            mode: strings.entryLabel,
            lists: strings.entryLists,
            lines: strings.entryLines,
            x: strings.xLabel,
            y: strings.yLabel,
            xPlaceholder: strings.xPlaceholder,
            yPlaceholder: strings.yPlaceholder,
            pairs: strings.pairsLabel,
            pairsPlaceholder: strings.pairsPlaceholder,
          }}
          pairsHint={
            <>
              {fmt(data.pairCount)}
              {tPlural(strings.pairsCount, locale, data.pairCount)}
              {data.skippedLines > 0
                ? tPlural(strings.skippedCount, locale, data.skippedLines, {
                    count: fmt(data.skippedLines),
                  })
                : null}
            </>
          }
          xHint={count(data.listXCount)}
          yHint={count(data.listYCount)}
        />
        <div className="grid items-end gap-4 sm:grid-cols-2">
          <SelectField
            id="kt-alternative"
            label={strings.alternativeLabel}
            onChange={setAlternative}
            options={[
              { value: "two-sided", label: strings.altTwoSided },
              { value: "greater", label: strings.altGreater },
              { value: "less", label: strings.altLess },
            ]}
            value={alternative}
          />
          <Checkbox
            checked={continuity}
            label={strings.continuity}
            onChange={setContinuity}
          />
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() =>
              data.fill({
                x: strings.exampleX,
                y: strings.exampleY,
                pairs: strings.examplePairs,
              })
            }
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() =>
              data.fill({
                x: strings.exampleTiesX,
                y: strings.exampleTiesY,
                pairs: strings.exampleTiesPairs,
              })
            }
            type="button"
          >
            {strings.tryTiesExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={data.clear}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </section>

      {showMismatch ? (
        <Notice>
          {t(strings.mismatch, {
            x: fmt(data.listXCount),
            y: fmt(data.listYCount),
          })}
        </Notice>
      ) : null}

      {report ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite" className="space-y-4">
            <StatGrid>
              <StatCard
                emphasis
                label={strings.tauB}
                value={num(report.tauB)}
              />
              <StatCard label={strings.tauA} value={num(report.tauA)} />
              <StatCard
                label={strings.pValue}
                value={formatPValue(fmt, report.p)}
              />
              <StatCard
                label={strings.concordantCard}
                value={fmt(report.counts.concordant)}
              />
              <StatCard
                label={strings.discordantCard}
                value={fmt(report.counts.discordant)}
              />
              <StatCard
                label={report.z === null ? strings.exactTest : strings.zScore}
                value={report.z === null ? strings.exactLabel : num(report.z)}
              />
            </StatGrid>
            <Explanation>{interpretation(report, fmt, strings)}</Explanation>
            <Explanation>
              {t(
                report.p < 0.05 ? strings.significant : strings.notSignificant,
                { p: formatPRelation(fmt, report.p) }
              )}
            </Explanation>
            <Explanation>
              {report.method === "exact"
                ? t(strings.methodExact, { n: fmt(report.n) })
                : strings.methodNormal}
            </Explanation>
            <div className="flex flex-wrap items-center gap-3">
              <p className="font-mono text-foreground text-sm">
                <span className="text-muted-foreground">
                  {strings.apaLabel}:{" "}
                </span>
                {apa}
              </p>
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copyResult}
                small
                text={apa}
              />
            </div>
          </div>
          <div className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.pairTableTitle}</h3>
            <ResultTable
              footer={[strings.kindTotal, fmt(report.pairs)]}
              head={[strings.colKind, strings.colCount]}
              rows={[
                [strings.kindConcordant, fmt(report.counts.concordant)],
                [strings.kindDiscordant, fmt(report.counts.discordant)],
                [strings.kindTiedX, fmt(report.counts.tiedX)],
                [strings.kindTiedY, fmt(report.counts.tiedY)],
                [strings.kindTiedBoth, fmt(report.counts.tiedBoth)],
              ]}
              wrapFirstColumn
            />
          </div>
          <div className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.plotTitle}</h3>
            <XYPlot
              fmt={fmt}
              label={t(strings.plotLabel, { n: fmt(report.n) })}
              series={[
                {
                  kind: "points",
                  xs: data.xs,
                  ys: data.ys,
                  color: 0,
                  label: strings.seriesPoints,
                },
              ]}
              xLabel={strings.axisX}
              yLabel={strings.axisY}
            />
          </div>
          {report.n <= 10 ? (
            <div className="space-y-2">
              <h3 className={WIDGET_H3}>{strings.listTitle}</h3>
              <ResultTable
                head={[strings.colPair, strings.colObs, strings.colResult]}
                rows={classifyPairs(data.xs, data.ys).map((row) => [
                  t(strings.pairIndex, {
                    i: fmt(row.i + 1),
                    j: fmt(row.j + 1),
                  }),
                  t(strings.pairObs, {
                    x1: num(data.xs[row.i]),
                    y1: num(data.ys[row.i]),
                    x2: num(data.xs[row.j]),
                    y2: num(data.ys[row.j]),
                  }),
                  kindLabel(row.kind, strings),
                ])}
                wrapFirstColumn
              />
            </div>
          ) : null}
          <div className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.compareTitle}</h3>
            <ResultTable
              head={[strings.colCoefficient, strings.colValue]}
              rows={[
                [strings.rowSpearman, num(report.spearmanRho)],
                [strings.rowPearson, num(report.pearsonR)],
              ]}
              wrapFirstColumn
            />
            <Explanation>{strings.compareNote}</Explanation>
          </div>
          <Working
            continuity={continuity}
            fmt={fmt}
            report={report}
            strings={strings}
          />
        </section>
      ) : null}
      {report || showMismatch ? null : <Notice>{strings.enterPrompt}</Notice>}
    </>
  );
}
